import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { MembersManagementActiveFilter } from '@/components/common/members-management';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import {
  allCompanyMemberColumns,
  buildCompanyMemberColumns,
  type CompanyMemberColumnKey,
  type CompanyMemberSortBy,
  type CompanyMemberSortDirection,
} from './company-member-display';
import type {
  RoleFilter,
  StatusFilter,
  VerificationFilter,
} from './CompanyMembersFilterSheet';
import { listCompanyMembers, type CompanyMembersScope } from './company-api';
import { companyMembersQueryKeys } from './company-members-query';

export function useCompanyMembersList(scope: CompanyMembersScope) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 280);
  const [role, setRole] = useState<RoleFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [verification, setVerification] = useState<VerificationFilter>('all');
  const [sortBy, setSortBy] = useState<CompanyMemberSortBy>('createdAt');
  const [sortDirection, setSortDirection] = useState<CompanyMemberSortDirection>('desc');
  const [visibleColumns, setVisibleColumns] = useState<ReadonlySet<CompanyMemberColumnKey>>(
    () => new Set(allCompanyMemberColumns),
  );
  const query = {
    page,
    pageSize,
    role,
    search: debouncedSearch || undefined,
    sortBy,
    sortDirection,
    status,
    verification,
  } as const;
  const queryKey = companyMembersQueryKeys.list(scope, query);
  const membersQuery = useQuery({
    queryFn: () => listCompanyMembers(query, scope),
    queryKey,
  });

  const resetPage = useCallback((action: () => void) => {
    action();
    setPage(1);
  }, []);
  const handleColumnSort = useCallback((nextSortBy: CompanyMemberSortBy) => {
    if (nextSortBy === sortBy) {
      setSortDirection((current) => current === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(nextSortBy);
      setSortDirection(nextSortBy === 'createdAt' ? 'desc' : 'asc');
    }
    setPage(1);
  }, [sortBy]);
  const columns = useMemo(() => buildCompanyMemberColumns({
    direction: sortDirection,
    onSort: handleColumnSort,
    sortBy,
    visible: visibleColumns,
  }), [handleColumnSort, sortBy, sortDirection, visibleColumns]);
  const activeFilters = useMemo<MembersManagementActiveFilter[]>(() => [
    ...(role !== 'all' ? [{
      key: 'role',
      label: role === 'admin' ? 'Administrateurs' : 'Membres',
      onClear: () => resetPage(() => setRole('all')),
    }] : []),
    ...(status !== 'all' ? [{
      key: 'status',
      label: status === 'active' ? 'Actifs' : 'Désactivés',
      onClear: () => resetPage(() => setStatus('all')),
    }] : []),
    ...(verification !== 'all' ? [{
      key: 'verification',
      label: verification === 'verified' ? 'Vérifiés' : 'Invitation en attente',
      onClear: () => resetPage(() => setVerification('all')),
    }] : []),
  ], [resetPage, role, status, verification]);

  const resetFilters = useCallback(() => resetPage(() => {
    setRole('all');
    setStatus('all');
    setVerification('all');
  }), [resetPage]);
  const resetControls = useCallback(() => {
    setPage(1);
    setSearch('');
    setRole('all');
    setStatus('all');
    setVerification('all');
    setSortBy('createdAt');
    setSortDirection('desc');
  }, []);
  const toggleColumn = useCallback((key: CompanyMemberColumnKey) => {
    setVisibleColumns((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  return {
    activeFilters, columns, membersQuery, page, pageSize, query, queryKey, role, search,
    sortBy, sortDirection, status, verification, visibleColumns,
    onDirectionChange: (value: CompanyMemberSortDirection) => resetPage(() => setSortDirection(value)),
    onPageChange: setPage,
    onPageSizeChange: (value: number) => resetPage(() => setPageSize(value)),
    onRoleChange: (value: RoleFilter) => resetPage(() => setRole(value)),
    onSearchChange: (value: string) => resetPage(() => setSearch(value)),
    onSortChange: (value: CompanyMemberSortBy) => resetPage(() => setSortBy(value)),
    onStatusChange: (value: StatusFilter) => resetPage(() => setStatus(value)),
    onVerificationChange: (value: VerificationFilter) => resetPage(() => setVerification(value)),
    resetFilters,
    resetControls,
    toggleColumn,
  };
}
